import React, { forwardRef, Ref } from 'react'
import styled from 'styled-components'
import { Button as ReakitButton, ButtonHTMLProps } from 'reakit/Button'
import { space, SpaceProps } from 'styled-system'
import { buttons, buttonSizes, textStyle } from '@zuto/utilities'
import { ButtonSize, ButtonVariant, TextStyle } from '@zuto/types'

interface StyledButtonProps extends ButtonHTMLProps {
  size: ButtonSize
  $variant: ButtonVariant
}

export interface ButtonProps extends ButtonHTMLProps, SpaceProps {
  /**
   * Text to display directly above 'subText' on a single line
   */
  subTextLineOne?: string
  /**
   * Text to display under the main text. Only works for variants
   * `primary` and `secondary` using size `large`
   */
  subText?: string
  /** Changes the present size of the button */
  size?: ButtonSize
  /** Select the colour variant of the button */
  variant?: ButtonVariant
  /** SVG to render inside the button.
   * Places on the right for `primary` and `secondary`.
   * Places on the left for `tertiary`
   * Only renders for `large` and `medium` sized buttons
   */
  icon?: React.ReactElement
  /** Disables the button */
  disabled?: boolean
}

const StyledButton = styled(ReakitButton) <StyledButtonProps>`
  display: flex;
  align-items: center;
  svg {
    margin-left: auto;
  }
  cursor: pointer;
  ${buttonSizes}
  ${buttons}
  ${space}
`

interface SubTextProps extends React.HtmlHTMLAttributes<HTMLSpanElement> {
  textStyle: TextStyle
}

const SubText = styled.span<SubTextProps>`
  ${textStyle}
`

const TextWrapper = styled.span<{ hasIcon?: boolean }>`
  display: flex;
  flex-direction: column;
  ${(props) => (props.hasIcon ? 'margin-right: 16px;' : '')}
`

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  subTextLineOne,
  subText,
  variant = 'primary',
  size = 'medium',
  icon,
  ...props
}: ButtonProps, ref: Ref<HTMLButtonElement>): JSX.Element => (
  <StyledButton ref={ref} $variant={variant} size={size} {...props}>
    <TextWrapper hasIcon={!!(size !== 'small' && icon)}>
      {children}
      {((subText || subTextLineOne) && (size === 'large' || size === 'largeCenteredText')) && (
        <SubText data-testid="button-subtext" textStyle="body.smallPrint.regular">
          {subTextLineOne && (
            <>
              {subTextLineOne}
              <br />
            </>
          )}
          {subText}
        </SubText>
      )}
    </TextWrapper>
    {size !== 'small' && icon && React.cloneElement(icon, { 'data-testid': 'button-icon' })}
  </StyledButton>
))

export default Button
